/**
 * Create Request / Request Detail scroll + footer layout constants.
 * Native footers are in-flow (flex column); web keeps content padding above footer.
 */
import { resolveBottomSafePadding } from '../styles/safeArea';
import { spacing } from '../theme/spacing';
import { lineHeight } from '../theme/typography';

export const REQUEST_FORM_WEB_SCROLL_PADDING_BOTTOM = 80;

/** Keyboard clearance for focused inputs (KeyboardAwareFormScrollView bottomOffset). */
export const REQUEST_FORM_KEYBOARD_BOTTOM_OFFSET = 100;

/** Removed legacy native mainContent padding — was double-reserving space above absolute footer. */
export const LEGACY_NATIVE_MAIN_CONTENT_FOOTER_RESERVE_PX = 100;

/** Vertical padding above/below the native request footer button row. */
export const REQUEST_FORM_NATIVE_FOOTER_VERTICAL_PADDING = 12;

/**
 * Native Create Request / Request Detail in-flow footer row height
 * (vertical padding + button row), excluding home-indicator inset.
 */
export const REQUEST_FORM_NATIVE_FOOTER_CONTENT_HEIGHT =
  REQUEST_FORM_NATIVE_FOOTER_VERTICAL_PADDING * 2 + 44;

/** Extra clearance for rich-text WebView fields (toolbar + editor + in-flow footer). */
export const REQUEST_FORM_RICH_TEXT_KEYBOARD_BOTTOM_OFFSET =
  REQUEST_FORM_KEYBOARD_BOTTOM_OFFSET +
  REQUEST_FORM_NATIVE_FOOTER_CONTENT_HEIGHT +
  48;

/** RN Done bar sits above the keyboard; Keyboard events do not include it. */
export const REQUEST_FORM_RICH_TEXT_DONE_BAR_HEIGHT = 44;

/** Keep the field / section label above the toolbar when the editor focuses. */
export const REQUEST_FORM_RICH_TEXT_LABEL_CLEARANCE = 56;

/**
 * Bottom padding for the native request footer safe-area region only.
 * Button row vertical breathing room lives in
 * {@link REQUEST_FORM_NATIVE_FOOTER_CONTENT_HEIGHT} (centered inner band).
 */
export function getRequestFormNativeFooterPaddingBottom(
  bottomInset: number,
): number {
  return resolveBottomSafePadding(bottomInset);
}

/** Breathing room between pinned sidebar footer nav icons and the request footer band. */
export const REQUEST_FORM_SIDEBAR_FOOTER_NAV_EXTRA_PADDING = 8;

/**
 * Upward offset for pinned sidebar footer nav (Supporting Documents, Summary) when
 * embedded beside the native request footer band. Subtracts from
 * {@link getEmbeddedSidebarFooterNavPaddingBottom} so icons sit closer to the footer
 * row without overlapping the 68px footer content band + safe-area inset.
 *
 * ~32px from design tokens: {@link spacing.pdGridGutter} + {@link spacing.sm}.
 */
export const REQUEST_FORM_EMBEDDED_SIDEBAR_FOOTER_NAV_LIFT =
  spacing.pdGridGutter + spacing.sm;

/** Default inset below pinned footer nav on web expanded sidebar (matches navFooterPinned base). */
export const REQUEST_FORM_WEB_SIDEBAR_FOOTER_NAV_BASE_PADDING =
  spacing.belowFormStepPageTitleRelaxed;

/**
 * Bottom padding for web pinned footer nav (Supporting Documents, Summary).
 * Base inset plus {@link REQUEST_FORM_EMBEDDED_SIDEBAR_FOOTER_NAV_LIFT} so items sit above the viewport bottom.
 */
export const REQUEST_FORM_WEB_SIDEBAR_FOOTER_NAV_PADDING_BOTTOM =
  REQUEST_FORM_WEB_SIDEBAR_FOOTER_NAV_BASE_PADDING +
  REQUEST_FORM_EMBEDDED_SIDEBAR_FOOTER_NAV_LIFT;

/**
 * Shared height for sidebar header controls (+ Add Scope, collapse chevron).
 * Matches compact form action padding + one-line label line box.
 */
export const REQUEST_FORM_SIDEBAR_HEADER_CONTROL_HEIGHT =
  spacing.formCompactActionPaddingVertical * 2 + lineHeight.formControl;

/**
 * Viewport width below which the request shell uses the mobile layout (`SidebarPreview`
 * rail + overlay drawer) instead of the embedded `Sidebar`. Consumed by `App.tsx`.
 *
 * #1438: `spacing.formMaxWidth` is derived from this value, so the two must move
 * together. `theme/__tests__/form-max-width.test.ts` imports this constant rather than
 * mirroring the number, so changing it here fails that test instead of silently
 * unbinding the form-column cap.
 */
export const REQUEST_FORM_MOBILE_LAYOUT_MAX_WIDTH = 1200;

/** Collapsed icon rail (native preview + web/mobile collapsed sidebar). */
export const REQUEST_FORM_SIDEBAR_COLLAPSED_WIDTH = 60;

/** Expanded drawer with step labels on web desktop. */
export const REQUEST_FORM_SIDEBAR_EXPANDED_WIDTH_WEB = 252;

/**
 * Expanded drawer with step labels on native/mobile overlay.
 * Fits longest step titles (e.g. Supporting Documents) without spanning most of the screen.
 */
export const REQUEST_FORM_SIDEBAR_EXPANDED_WIDTH_NATIVE = 288;

/** Expanded sidebar width for the current platform. */
export function getRequestFormSidebarExpandedWidth(isWeb: boolean): number {
  return isWeb
    ? REQUEST_FORM_SIDEBAR_EXPANDED_WIDTH_WEB
    : REQUEST_FORM_SIDEBAR_EXPANDED_WIDTH_NATIVE;
}

/**
 * Bottom padding for sidebar footer nav when the rail shares a row with the request footer.
 *
 * Formula: footer content band (68px) + safe-area inset + extra spacing − lift offset.
 */
export function getEmbeddedSidebarFooterNavPaddingBottom(
  safeBottomPadding: number,
): number {
  return (
    REQUEST_FORM_NATIVE_FOOTER_CONTENT_HEIGHT +
    safeBottomPadding +
    REQUEST_FORM_SIDEBAR_FOOTER_NAV_EXTRA_PADDING -
    REQUEST_FORM_EMBEDDED_SIDEBAR_FOOTER_NAV_LIFT
  );
}
